import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { auth as firebaseAuth, isFirebaseConfigured } from '../firebaseConfig';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { COUNTRIES } from '../utils/countries';
import './AuthPage.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="#25D366" style={{ flexShrink: 0 }}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.196 8.196 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24h-.19zm-3.6 3.6c-.2 0-.44.07-.67.33-.23.26-.88.86-.88 2.1 0 1.24.9 2.44 1.03 2.61.13.17 1.77 2.7 4.29 3.79.6.26 1.07.41 1.44.53.6.19 1.15.16 1.59.1.48-.07 1.48-.6 1.69-1.19.21-.58.21-1.08.15-1.19-.06-.1-.23-.17-.49-.3-.26-.13-1.48-.73-1.71-.81-.23-.08-.4-.13-.57.13-.17.26-.66.81-.81.98-.15.17-.3.19-.56.06-.26-.13-1.1-.41-2.09-1.3-.77-.69-1.29-1.54-1.44-1.8-.15-.26-.02-.4.11-.53.12-.12.26-.31.39-.46.13-.15.17-.26.26-.43.09-.17.04-.33-.02-.46-.07-.13-.57-1.37-.78-1.88-.2-.49-.41-.43-.57-.44l-.49-.01z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" style={{ flexShrink: 0 }}>
    <defs>
      <linearGradient id="ap-ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </linearGradient>
    </defs>
    <path
      fill="url(#ap-ig-grad)"
      d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
    />
  </svg>
);

const ShopifyIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="#96BF48" style={{ flexShrink: 0 }}>
    <path d="M15.33 4.14c-.03-.23-.2-.4-.43-.42-.23-.02-1.62-.05-1.62-.05s-1.07-1.07-1.2-1.2c-.13-.13-.38-.08-.47-.03-.02.01-.58.33-1.47.84-.25-.66-.63-1.32-1.22-1.81C7.8.44 6.64.67 6.13 1.06c-.75.57-.95 1.54-.6 2.87-1.1.37-1.88.63-1.92.65-.59.2-.61.22-.68.82-.07.56-1.57 12.06-1.57 12.06L12.5 20l8.13-2.16s-5.27-13.48-5.3-13.7zm-4.31-.07l1.04-.35c.16.51.27 1.1.32 1.74l-1.92.65c.12-.76.32-1.48.56-2.04zm-1.87.65c.34-.26.96-.44 1.57-.14.28.14.5.37.66.67-.32.74-.59 1.67-.74 2.66l-1.89.64c-.17-.99-.07-2.42.4-2.83z" />
  </svg>
);

const OdooIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="#A24689" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="9" stroke="#A24689" strokeWidth="2.5" fill="none" />
    <circle cx="12" cy="12" r="4.5" fill="#A24689" />
  </svg>
);

const ZohoIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" style={{ flexShrink: 0 }}>
    <rect x="3" y="4" width="7" height="7" rx="1.5" fill="#E42528" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" fill="#226CE0" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" fill="#2CA01C" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" fill="#F49A00" />
  </svg>
);

export default function AuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const isRegisterRoute = location.pathname === '/register';
  const [isRegister, setIsRegister] = useState(isRegisterRoute);
  const [loading, setLoading] = useState(false);

  // Password visibility states
  const [showPwd, setShowPwd] = useState(false);
  const [showCPwd, setShowCPwd] = useState(false);
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  // Login form state (Remember me is always ON by default)
  const [loginForm, setLoginForm] = useState(() => {
    const savedEmail = localStorage.getItem('remembered_email') || '';
    return { email: savedEmail, password: '', remember: true };
  });

  // Register form state
  const [regForm, setRegForm] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    confirmPassword: '',
    countryDial: '+91',
    countryCode: 'IN',
  });

  // Progressive flow & OTP States
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  // Refs for 6-box OTP input
  const otpInputRefs = useRef([]);
  const isSendingOtpRef = useRef(false);
  const isVerifyingOtpRef = useRef(false);
  const isSendingForgotOtpRef = useRef(false);

  // Forgot Password Modal State
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotMethod, setForgotMethod] = useState('mobile'); // 'mobile' | 'email'
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotMobile, setForgotMobile] = useState('');
  const [forgotCountryCode, setForgotCountryCode] = useState('IN');
  const [forgotCountryDial, setForgotCountryDial] = useState('+91');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotConfirmationResult, setForgotConfirmationResult] = useState(null);
  const [forgotResetToken, setForgotResetToken] = useState('');
  const [forgotResendTimer, setForgotResendTimer] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmNewPwd, setShowConfirmNewPwd] = useState(false);

  // Resend OTP countdown timer for forgot password
  useEffect(() => {
    let interval = null;
    if (forgotResendTimer > 0) {
      interval = setInterval(() => {
        setForgotResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [forgotResendTimer]);

  // Sync route
  useEffect(() => {
    setIsRegister(location.pathname === '/register');
  }, [location.pathname]);

  // Handle direct reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('resetToken');
    const urlEmail = params.get('email');
    if (urlToken && urlEmail) {
      setForgotResetToken(urlToken);
      setForgotEmail(urlEmail.trim());
      setForgotMethod('email');
      setForgotStep(2);
      setShowForgotModal(true);
    }
  }, [location.search]);

  // Resend OTP countdown timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Clean up any existing reCAPTCHA instance and dynamic DOM containers
  const cleanupRecaptcha = () => {
    if (window.recaptchaWidgetId !== undefined && window.grecaptcha?.reset) {
      try {
        window.grecaptcha.reset(window.recaptchaWidgetId);
      } catch (_) {}
      window.recaptchaWidgetId = undefined;
    }

    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (_) {}
      window.recaptchaVerifier = null;
    }

    try {
      const oldContainers = document.querySelectorAll('.dynamic-recaptcha-slot');
      oldContainers.forEach(el => {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    } catch (_) {}
  };

  // Cleanup reCAPTCHA on unmount
  useEffect(() => {
    return () => {
      cleanupRecaptcha();
    };
  }, []);

  // Progressive unlock conditions
  const isNameFilled = regForm.name.trim().length >= 2;
  const rawMobileDigits = regForm.mobile.trim().replace(/\D/g, '');
  const isMobileFilled = rawMobileDigits.length >= 6;

  // Create a 100% fresh invisible reCAPTCHA in a brand-new DOM node every time
  const getRecaptchaVerifier = async () => {
    cleanupRecaptcha();

    const uniqueId = `recaptcha-slot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const container = document.createElement('div');
    container.id = uniqueId;
    container.className = 'dynamic-recaptcha-slot';
    document.body.appendChild(container);

    const verifier = new RecaptchaVerifier(firebaseAuth, uniqueId, {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        cleanupRecaptcha();
      },
      'error-callback': () => {
        cleanupRecaptcha();
      },
    });

    window.recaptchaVerifier = verifier;
    try {
      window.recaptchaWidgetId = await verifier.render();
    } catch (_) {}

    return verifier;
  };

  // Helper: Safely build E.164 phone number without stripping '91' from 10-digit numbers starting with 91
  const buildFullPhoneNumber = (rawInput, countryCode, countryDial) => {
    const dial = countryDial || '+91';
    const cleanDial = dial.replace(/\D/g, '');
    const selectedCountry =
      COUNTRIES.find(c => c.code === countryCode) ||
      COUNTRIES.find(c => c.dial === dial) ||
      COUNTRIES[0];
    const expectedLen = selectedCountry?.phoneLen || 10;

    let cleanMobile = String(rawInput || '').replace(/\D/g, '').replace(/^0+/, '');
    if (
      cleanMobile.startsWith(cleanDial) &&
      cleanMobile.length === cleanDial.length + expectedLen
    ) {
      cleanMobile = cleanMobile.slice(cleanDial.length);
    }
    return { dial, cleanMobile, fullPhoneNumber: `${dial}${cleanMobile}` };
  };

  // 1. Send OTP to Mobile (Protected against duplicate calls & reusable unlimited times)
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (isSendingOtpRef.current) return;
    if (!isNameFilled) {
      toast.error('Please enter your full name first');
      return;
    }
    if (!isMobileFilled) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    isSendingOtpRef.current = true;
    setOtpLoading(true);
    setOtpError(false);
    const { dial, fullPhoneNumber } = buildFullPhoneNumber(
      rawMobileDigits,
      regForm.countryCode,
      regForm.countryDial
    );

    try {
      // Pre-check if mobile number is already registered in database before sending SMS OTP
      try {
        await api.post('/auth/check-mobile', {
          mobile: fullPhoneNumber,
          countryDial: dial,
          countryCode: regForm.countryCode,
        });
      } catch (checkErr) {
        if (checkErr?.response?.status === 400) {
          toast.error(checkErr.response?.data?.message || 'This mobile number is already registered. Please sign in.');
          return;
        }
      }

      if (isFirebaseConfigured) {
        let confirmation;
        try {
          const appVerifier = await getRecaptchaVerifier();
          confirmation = await signInWithPhoneNumber(firebaseAuth, fullPhoneNumber, appVerifier);
        } catch (firstErr) {
          if (/recaptcha|rendered|timeout|expired/i.test(firstErr?.message || '')) {
            cleanupRecaptcha();
            const retryVerifier = await getRecaptchaVerifier();
            confirmation = await signInWithPhoneNumber(firebaseAuth, fullPhoneNumber, retryVerifier);
          } else {
            throw firstErr;
          }
        }
        setConfirmationResult(confirmation);
        setOtpSent(true);
        setResendTimer(30);
        setOtpDigits(['', '', '', '', '', '']);
        toast.success(`OTP sent to ${fullPhoneNumber} via SMS!`);
        setTimeout(() => otpInputRefs.current[0]?.focus(), 150);
      } else {
        // Development / Demo Fallback Mode
        setOtpSent(true);
        setResendTimer(30);
        setOtpDigits(['', '', '', '', '', '']);
        toast.success(`(Demo Mode) OTP code 123456 sent to ${fullPhoneNumber}!`);
        setTimeout(() => otpInputRefs.current[0]?.focus(), 150);
      }
    } catch (err) {
      console.error('[PHONE AUTH ERROR]', err);
      cleanupRecaptcha();
      const errMsg = err?.code === 'auth/invalid-app-credential'
        ? 'Firebase security check failed. Check Authorized Domains in Firebase Console.'
        : (err.message || 'Failed to send OTP. Please check mobile number.');
      toast.error(errMsg);
    } finally {
      setOtpLoading(false);
      setTimeout(() => {
        isSendingOtpRef.current = false;
      }, 800);
    }
  };

  // 2. Handle OTP Digits change & Auto-Focus
  const handleOtpBoxChange = (index, val) => {
    const cleanVal = val.replace(/\D/g, '');
    const newDigits = [...otpDigits];

    if (cleanVal.length > 1) {
      const pasted = cleanVal.slice(0, 6).split('');
      pasted.forEach((char, i) => {
        newDigits[i] = char;
      });
      setOtpDigits(newDigits);
      const nextIdx = Math.min(pasted.length, 5);
      otpInputRefs.current[nextIdx]?.focus();

      if (pasted.length === 6) {
        verifyOtpCode(newDigits.join(''));
      }
      return;
    }

    newDigits[index] = cleanVal;
    setOtpDigits(newDigits);
    setOtpError(false);

    if (cleanVal && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    if (newDigits.every(d => d !== '') && newDigits.join('').length === 6) {
      verifyOtpCode(newDigits.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // 3. Verify OTP Code (Protected against concurrent verification)
  const verifyOtpCode = async (code) => {
    const fullOtp = (code || otpDigits.join('')).trim();
    if (fullOtp.length !== 6) return;
    if (isVerifyingOtpRef.current) return;

    isVerifyingOtpRef.current = true;
    setOtpLoading(true);
    try {
      if (isFirebaseConfigured && confirmationResult) {
        await confirmationResult.confirm(fullOtp);
      } else {
        if (fullOtp !== '123456' && isFirebaseConfigured) {
          throw new Error('Invalid OTP');
        }
      }
      setIsMobileVerified(true);
      setOtpError(false);
      toast.success('Mobile verified successfully! ✓');
    } catch (err) {
      console.error('[VERIFY OTP ERROR]', err);
      setOtpError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 650);
      toast.error('Invalid OTP code. Please enter the correct code.');
    } finally {
      setOtpLoading(false);
      setTimeout(() => {
        isVerifyingOtpRef.current = false;
      }, 600);
    }
  };

  // 4. Final Register Submission
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!isMobileVerified) {
      toast.error('Please verify your mobile number with OTP first');
      return;
    }
    if (!regForm.password || regForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (regForm.password !== regForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const emailTrimmed = regForm.email.trim();
    if (emailTrimmed && !EMAIL_REGEX.test(emailTrimmed)) {
      toast.error('Enter a valid email address (or leave it blank)');
      return;
    }

    setLoading(true);
    const { fullPhoneNumber: formattedMobile } = buildFullPhoneNumber(
      rawMobileDigits,
      regForm.countryCode,
      regForm.countryDial
    );

    try {
      const selectedCountry = COUNTRIES.find(c => c.code === regForm.countryCode) ||
        COUNTRIES.find(c => c.dial === regForm.countryDial) ||
        COUNTRIES[0];

      const { data } = await api.post('/auth/register', {
        name: regForm.name.trim(),
        mobile: formattedMobile,
        ...(emailTrimmed ? { email: emailTrimmed } : {}),
        password: regForm.password,
        countryCode: selectedCountry.code,
        countryName: selectedCountry.name,
        countryDial: selectedCountry.dial,
        currency: selectedCountry.currency,
        currencySymbol: selectedCountry.symbol,
      });

      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}! Account created! 🎉`);
      navigate('/tutorial');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // 5. Handle Login Submission
  const handleLogin = async (e) => {
    e.preventDefault();
    const loginInput = (loginForm.email || '').trim();
    if (!loginInput) {
      toast.error('Mobile number or Email address is required');
      return;
    }
    if (!loginForm.password) {
      toast.error('Password is required');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        identifier: loginInput,
        password: loginForm.password,
      });

      if (loginForm.remember) {
        localStorage.setItem('remembered_email', loginInput);
      } else {
        localStorage.removeItem('remembered_email');
      }

      login(data.token, data.user);
      navigate(data.user.role === 'superadmin' ? '/superadmin/overview' : '/tutorial');
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handlers
  const handleRequestForgotOtp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSendingForgotOtpRef.current) return;

    if (forgotMethod === 'mobile') {
      const { cleanMobile, fullPhoneNumber } = buildFullPhoneNumber(
        forgotMobile,
        forgotCountryCode,
        forgotCountryDial
      );
      if (cleanMobile.length < 6) {
        toast.error('Please enter a valid mobile number');
        return;
      }

      isSendingForgotOtpRef.current = true;
      setForgotLoading(true);
      try {
        // 1. Verify account exists on backend & generate reset session
        const { data } = await api.post('/auth/forgot-password', { mobile: fullPhoneNumber });
        setForgotResetToken(data.resetToken || '');

        // 2. Send SMS OTP via Firebase (or development fallback)
        if (isFirebaseConfigured) {
          let confirmation;
          try {
            const appVerifier = await getRecaptchaVerifier();
            confirmation = await signInWithPhoneNumber(firebaseAuth, fullPhoneNumber, appVerifier);
          } catch (firstErr) {
            if (/recaptcha|rendered|timeout|expired/i.test(firstErr?.message || '')) {
              cleanupRecaptcha();
              const retryVerifier = await getRecaptchaVerifier();
              confirmation = await signInWithPhoneNumber(firebaseAuth, fullPhoneNumber, retryVerifier);
            } else {
              throw firstErr;
            }
          }
          setForgotConfirmationResult(confirmation);
          toast.success(`Reset OTP sent to ${fullPhoneNumber} via SMS!`);
        } else {
          toast.success(`(Demo Mode) Reset OTP 123456 sent to ${fullPhoneNumber}!`);
        }

        setForgotResendTimer(30);
        setForgotStep(2);
      } catch (err) {
        console.error('[FORGOT PHONE OTP ERROR]', err);
        cleanupRecaptcha();
        const errMsg = err.response?.data?.message || err.message || 'Failed to send reset OTP';
        toast.error(errMsg);
      } finally {
        setForgotLoading(false);
        setTimeout(() => {
          isSendingForgotOtpRef.current = false;
        }, 800);
      }
    } else {
      // Email method
      const emailTrimmed = forgotEmail.trim();
      if (!emailTrimmed || !EMAIL_REGEX.test(emailTrimmed)) {
        toast.error('Enter your registered email address');
        return;
      }
      setForgotLoading(true);
      try {
        const { data } = await api.post('/auth/forgot-password', { email: emailTrimmed });
        setForgotResetToken(data.resetToken || '');
        toast.success(data.message || 'Reset OTP sent to your email!');
        setForgotResendTimer(60);
        setForgotStep(2);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to send reset email');
      } finally {
        setForgotLoading(false);
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const otpTrimmed = forgotOtp.trim();
    if (!otpTrimmed) {
      toast.error('Enter the 6-digit OTP code');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setForgotLoading(true);
    try {
      if (forgotMethod === 'mobile') {
        const cleanMobile = forgotMobile.trim().replace(/\D/g, '');
        const fullPhoneNumber = `${forgotCountryDial}${cleanMobile}`;

        // Verify Firebase phone OTP on client
        if (isFirebaseConfigured && forgotConfirmationResult) {
          await forgotConfirmationResult.confirm(otpTrimmed);
        } else {
          if (otpTrimmed !== '123456' && isFirebaseConfigured) {
            throw new Error('Invalid OTP code');
          }
        }

        const { data } = await api.post('/auth/reset-password', {
          mobile: fullPhoneNumber,
          otp: otpTrimmed,
          resetToken: forgotResetToken,
          newPassword,
        });

        toast.success(data.message || 'Password reset successfully! You can now log in.');
        setShowForgotModal(false);
        setLoginForm(prev => ({ ...prev, email: cleanMobile, password: newPassword }));
        setForgotStep(1);
      } else {
        const { data } = await api.post('/auth/reset-password', {
          email: forgotEmail.trim(),
          otp: otpTrimmed,
          resetToken: forgotResetToken,
          newPassword,
        });

        toast.success(data.message || 'Password reset successfully! You can now log in.');
        setShowForgotModal(false);
        setLoginForm(prev => ({ ...prev, email: forgotEmail.trim(), password: newPassword }));
        setForgotStep(1);
      }
    } catch (err) {
      console.error('[RESET PASSWORD ERROR]', err);
      const errMsg = err.response?.data?.message || (err.message?.includes('auth/') ? 'Invalid OTP code' : err.message) || 'Failed to reset password';
      toast.error(errMsg);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="ap-root">
      {/* ── System Dark Animated Background with Gold Glowing Particles ── */}
      <div className="ap-bg" aria-hidden="true">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`ap-particle ap-p${i + 1}`} />
        ))}
        <div className="ap-grid" />
      </div>

      {/* ONE SINGLE UNIFIED MASTER CONTAINER */}
      <div className="ap-unified-card">
        {/* ════════════ LEFT PANEL: Darker distinct background with Wati features ════════════ */}
        <div className="ap-left-panel">
          <div className="ap-brand-header">
            <div className="ap-logo-wrap">
              <img src="/logo.png" alt="XSquareHub" className="ap-logo-img" />
              <span className="ap-brand-name">XSquareHub</span>
            </div>
            <span className="ap-pill-badge">
              <i className="fa-solid fa-bolt" /> Official Meta Cloud API
            </span>
          </div>

          <h1 className="ap-hero-title">
            Business conversation made simple,{' '}
            <span className="ap-title-gold">powered by AI & WhatsApp</span>
          </h1>

          <div className="ap-features-list">
            <div className="ap-feature-item">
              <i className="fa-solid fa-circle-check ap-gold-check" />
              <span>
                <strong>High-Converting Campaigns:</strong> Broadcast targeted messages on WhatsApp (Meta Cloud API), SMS, WhatsApp Calling, and Automation.
              </span>
            </div>

            <div className="ap-feature-item">
              <i className="fa-solid fa-circle-check ap-gold-check" />
              <span>
                <strong>10x Productivity with AI:</strong> Drag & Drop Flow Builder, Chatbots, instant Catalog orders, and smart auto-replies.
              </span>
            </div>

            <div className="ap-feature-item">
              <i className="fa-solid fa-circle-check ap-gold-check" />
              <span>
                <strong>Multi-Agent Team Inbox:</strong> Collaborate, assign tickets, and convert leads with real-time customer context.
              </span>
            </div>

            <div className="ap-feature-item">
              <i className="fa-solid fa-circle-check ap-gold-check" />
              <span>
                <strong>Enterprise-Grade Delivery:</strong> 99.9% uptime, verified Meta green tick support, bank-grade encryption & security.
              </span>
            </div>
          </div>

          {/* Testimonial Quote */}
          <div className="ap-quote-glass">
            <p className="ap-quote-text">
              &ldquo;Our customer response rate grew by 4X and abandoned cart recovery jumped by 35% within 14 days of using XSquareHub WhatsApp marketing.&rdquo;
            </p>
            <div className="ap-quote-author">
              <i className="fa-solid fa-shield-halved" />
              <span>Verified Enterprise & D2C Brands</span>
            </div>
          </div>

          {/* Integrations Section */}
          <div className="ap-integrations-block">
            <div className="ap-integrations-label">
              Seamlessly integrated with top platforms:
            </div>
            <div className="ap-integration-badges">
              <div className="ap-int-badge">
                <WhatsAppIcon />
                <span>WhatsApp</span>
              </div>
              <div className="ap-int-badge">
                <InstagramIcon />
                <span>Instagram</span>
              </div>
              <div className="ap-int-badge">
                <ShopifyIcon />
                <span>Shopify</span>
              </div>
              <div className="ap-int-badge">
                <OdooIcon />
                <span>Odoo CRM</span>
              </div>
              <div className="ap-int-badge">
                <ZohoIcon />
                <span>Zoho CRM</span>
              </div>
            </div>
          </div>
        </div>

        {/* ════════════ RIGHT PANEL: Form Area inside the same container ════════════ */}
        <div className="ap-right-panel">
          {isRegister ? (
            /* ── REGISTER FORM ── */
            <div key="register-view" className="ap-card-body ap-anim-smooth">
                <div className="ap-card-head">
                  <h2 className="ap-heading">Start your free trial</h2>
                  <p className="ap-subheading">Get started with your demo account on XSquareHub</p>
                </div>

                <form className="ap-form" onSubmit={handleRegister} autoComplete="on">
                  {/* Step 1: Full Name */}
                  <div className="ap-field">
                    <label className="ap-label">
                      Full Name <span className="ap-req">*</span>
                    </label>
                    <div className="ap-input-wrap">
                      <i className="fa-regular fa-user ap-ico" />
                      <input
                        type="text"
                        placeholder="John Doe"
                        value={regForm.name}
                        onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                        required
                        autoComplete="name"
                      />
                      {isNameFilled && (
                        <i className="fa-solid fa-check ap-valid-icon" />
                      )}
                    </div>
                  </div>

                  {/* Step 2: Mobile Number (Enabled once Name is entered) */}
                  <div className={`ap-field ${!isNameFilled ? 'ap-field-locked' : ''}`}>
                    <div className="ap-label-row">
                      <label className="ap-label">
                        Mobile Number <span className="ap-req">* (OTP Verified)</span>
                      </label>
                      {!isNameFilled && (
                        <span className="ap-lock-hint">
                          <i className="fa-solid fa-lock" /> Enter name first
                        </span>
                      )}
                    </div>

                    <div className="ap-phone-row">
                      <div className="ap-dial-wrap">
                        <select
                          className="ap-dial-select"
                          value={regForm.countryCode || 'IN'}
                          disabled={!isNameFilled || isMobileVerified}
                          onChange={e => {
                            const selected = COUNTRIES.find(c => c.code === e.target.value) || COUNTRIES[0];
                            setRegForm({
                              ...regForm,
                              countryCode: selected.code,
                              countryDial: selected.dial,
                            });
                          }}
                        >
                          {COUNTRIES.map(c => (
                            <option key={`${c.code}-${c.dial}`} value={c.code}>
                              {c.flag} {c.dial}
                            </option>
                          ))}
                        </select>
                        <i className="fa-solid fa-chevron-down ap-dial-arrow" />
                      </div>

                      <div className="ap-input-wrap ap-phone-input-wrap">
                        <i className="fa-solid fa-mobile-screen ap-ico" />
                        <input
                          type="tel"
                          placeholder="98765 43210"
                          value={regForm.mobile}
                          disabled={!isNameFilled || isMobileVerified}
                          onChange={e => setRegForm({ ...regForm, mobile: e.target.value })}
                          required
                          maxLength={15}
                        />

                        {/* Inline Send / Resend OTP Button */}
                        {!isMobileVerified && (
                          <button
                            type="button"
                            className={`ap-send-otp-btn ${isMobileFilled && isNameFilled ? 'active' : ''}`}
                            disabled={!isNameFilled || !isMobileFilled || otpLoading || (otpSent && resendTimer > 0)}
                            onClick={handleSendOtp}
                          >
                            {otpLoading ? (
                              <span className="ap-mini-spinner" />
                            ) : otpSent ? (
                              resendTimer > 0 ? `${resendTimer}s` : 'Resend'
                            ) : (
                              'Send OTP'
                            )}
                          </button>
                        )}

                        {isMobileVerified && (
                          <span className="ap-verified-tag">
                            <i className="fa-solid fa-circle-check" /> Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Smart 6-Box OTP Input (Appears when OTP sent & not yet verified) */}
                  {otpSent && !isMobileVerified && (
                    <div className={`ap-otp-box-container ${isShaking ? 'ap-shake' : ''}`}>
                      <div className="ap-otp-top-bar">
                        <span>
                          Enter 6-digit OTP sent to <strong>{regForm.countryDial} {rawMobileDigits}</strong>
                        </span>
                        <button
                          type="button"
                          className="ap-edit-number-link"
                          onClick={() => {
                            cleanupRecaptcha();
                            setOtpSent(false);
                            setResendTimer(0);
                            setOtpDigits(['', '', '', '', '', '']);
                          }}
                        >
                          Edit Number
                        </button>
                      </div>

                      <div className="ap-otp-grid">
                        {otpDigits.map((digit, i) => (
                          <input
                            key={i}
                            ref={el => (otpInputRefs.current[i] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            className={`ap-otp-digit ${otpError ? 'error' : ''} ${digit ? 'filled' : ''}`}
                            value={digit}
                            onChange={e => handleOtpBoxChange(i, e.target.value)}
                            onKeyDown={e => handleOtpKeyDown(i, e)}
                          />
                        ))}
                      </div>

                      {otpError && (
                        <div className="ap-otp-err-text">
                          <i className="fa-solid fa-triangle-exclamation" /> Invalid OTP code. Please check and re-enter.
                        </div>
                      )}

                      <div className="ap-otp-bottom-bar">
                        <span className="ap-otp-didnt-get">
                          Didn&apos;t receive code?
                        </span>
                        <button
                          type="button"
                          className="ap-resend-link"
                          disabled={resendTimer > 0 || otpLoading}
                          onClick={handleSendOtp}
                        >
                          {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Email Address (Optional) */}
                  <div className={`ap-field ${!isMobileVerified ? 'ap-field-locked' : ''}`}>
                    <label className="ap-label">
                      Business Email Address <span className="ap-opt">(Optional)</span>
                    </label>
                    <div className="ap-input-wrap">
                      <i className="fa-regular fa-envelope ap-ico" />
                      <input
                        type="email"
                        placeholder="you@company.com"
                        value={regForm.email}
                        disabled={!isMobileVerified}
                        onChange={e => setRegForm({ ...regForm, email: e.target.value })}
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Step 5: Password (Enabled after Mobile is Verified) */}
                  <div className={`ap-field ${!isMobileVerified ? 'ap-field-locked' : ''}`}>
                    <div className="ap-label-row">
                      <label className="ap-label">
                        Password <span className="ap-req">*</span>
                      </label>
                      {!isMobileVerified && (
                        <span className="ap-lock-hint">
                          <i className="fa-solid fa-lock" /> Verify OTP to unlock
                        </span>
                      )}
                    </div>
                    <div className="ap-input-wrap">
                      <i className="fa-solid fa-lock ap-ico" />
                      <input
                        type={showPwd ? 'text' : 'password'}
                        placeholder="Minimum 6 characters"
                        value={regForm.password}
                        disabled={!isMobileVerified}
                        onChange={e => setRegForm({ ...regForm, password: e.target.value })}
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="ap-eye"
                        onClick={() => setShowPwd(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fa-regular ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Step 6: Confirm Password */}
                  <div className={`ap-field ${!isMobileVerified ? 'ap-field-locked' : ''}`}>
                    <label className="ap-label">
                      Confirm Password <span className="ap-req">*</span>
                    </label>
                    <div className="ap-input-wrap">
                      <i className="fa-solid fa-shield-halved ap-ico" />
                      <input
                        type={showCPwd ? 'text' : 'password'}
                        placeholder="Re-enter password"
                        value={regForm.confirmPassword}
                        disabled={!isMobileVerified}
                        onChange={e => setRegForm({ ...regForm, confirmPassword: e.target.value })}
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="ap-eye"
                        onClick={() => setShowCPwd(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fa-regular ${showCPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </button>
                      {regForm.confirmPassword && regForm.password === regForm.confirmPassword && (
                        <i className="fa-solid fa-check ap-valid-icon" style={{ right: 40 }} />
                      )}
                    </div>
                  </div>

                  <p className="ap-terms-text">
                    By signing up, you agree to our Terms of Service and Privacy Policy.
                  </p>

                  {/* Final Submit Button (Gold Gradient) */}
                  <button
                    type="submit"
                    className="ap-btn"
                    disabled={
                      loading ||
                      !isMobileVerified ||
                      !regForm.name.trim() ||
                      regForm.password.length < 6 ||
                      regForm.password !== regForm.confirmPassword
                    }
                  >
                    {loading ? (
                      <span className="ap-spinner" />
                    ) : (
                      <>
                        <span>Start My Free Trial</span>
                        <i className="fa-solid fa-arrow-right" />
                      </>
                    )}
                  </button>
                </form>

                {/* Switch to Login */}
                <div className="ap-switch">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="ap-switch-btn"
                    onClick={() => {
                      setIsRegister(false);
                      navigate('/login', { replace: true });
                    }}
                  >
                    Log In
                  </button>
                </div>
              </div>
            ) : (
              /* ── LOGIN FORM ── */
              <div key="login-view" className="ap-card-body ap-anim-smooth">
                <div className="ap-card-head">
                  <h2 className="ap-heading">Welcome Back</h2>
                  <p className="ap-subheading">Sign in to your XSquareHub account</p>
                </div>

                <form className="ap-form" onSubmit={handleLogin} autoComplete="on">
                  <div className="ap-field">
                    <label className="ap-label">
                      Mobile Number or Email <span className="ap-req">*</span>
                    </label>
                    <div className="ap-input-wrap">
                      <i className="fa-solid fa-user-check ap-ico" />
                      <input
                        type="text"
                        placeholder="9876543210 or you@company.com"
                        value={loginForm.email}
                        onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                        autoComplete="username"
                      />
                    </div>
                  </div>

                  <div className="ap-field">
                    <div className="ap-label-row">
                      <label className="ap-label">
                        Password <span className="ap-req">*</span>
                      </label>
                      <button
                        type="button"
                        className="ap-forgot"
                        onClick={() => {
                          const currentInput = (loginForm.email || '').trim();
                          if (currentInput.includes('@')) {
                            setForgotEmail(currentInput);
                            setForgotMethod('email');
                          } else {
                            const digits = currentInput.replace(/\D/g, '');
                            if (digits) {
                              setForgotMobile(digits.length > 10 ? digits.slice(-10) : digits);
                            }
                            setForgotMethod('mobile');
                          }
                          setForgotStep(1);
                          setForgotOtp('');
                          setNewPassword('');
                          setConfirmNewPassword('');
                          setShowForgotModal(true);
                        }}
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <div className="ap-input-wrap">
                      <i className="fa-solid fa-lock ap-ico" />
                      <input
                        type={showLoginPwd ? 'text' : 'password'}
                        placeholder="Enter password"
                        value={loginForm.password}
                        onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="ap-eye"
                        onClick={() => setShowLoginPwd(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fa-regular ${showLoginPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </button>
                    </div>
                  </div>

                  <div className="ap-remember">
                    <label className="ap-check-label">
                      <input
                        type="checkbox"
                        checked={loginForm.remember}
                        onChange={e => setLoginForm({ ...loginForm, remember: e.target.checked })}
                      />
                      <span className="ap-checkmark" />
                      Remember me
                    </label>
                  </div>

                  <button type="submit" className="ap-btn" disabled={loading}>
                    {loading ? (
                      <span className="ap-spinner" />
                    ) : (
                      <>
                        <i className="fa-solid fa-right-to-bracket" />
                        <span>Sign In</span>
                      </>
                    )}
                  </button>
                </form>

                <div className="ap-switch">
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    className="ap-switch-btn"
                    onClick={() => {
                      setIsRegister(true);
                      navigate('/register', { replace: true });
                    }}
                  >
                    Start Free Trial
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      <div className="ap-copyright">
        © {new Date().getFullYear()} XSquareHub. All rights reserved.
      </div>

      {/* Forgot / Reset Password Modal */}
      {showForgotModal && (
        <div className="ap-modal-overlay" onClick={() => setShowForgotModal(false)}>
          <div className="ap-modal-box" onClick={e => e.stopPropagation()}>
            <button className="ap-modal-close" onClick={() => setShowForgotModal(false)}>
              <i className="fa-solid fa-xmark" />
            </button>

            <div className="ap-modal-head">
              <h3 className="ap-modal-title">Reset Password</h3>
              <p className="ap-modal-sub">
                {forgotStep === 1
                  ? 'Choose your recovery method and enter details to receive an OTP'
                  : `Enter the 6-digit OTP code sent to ${
                      forgotMethod === 'mobile'
                        ? `${forgotCountryDial} ${forgotMobile}`
                        : forgotEmail
                    }`}
              </p>
            </div>

            {/* Method Tabs: Mobile OTP vs Email OTP (Step 1 only) */}
            {forgotStep === 1 && (
              <div className="ap-forgot-tabs">
                <button
                  type="button"
                  className={`ap-forgot-tab ${forgotMethod === 'mobile' ? 'active' : ''}`}
                  onClick={() => {
                    setForgotMethod('mobile');
                    setForgotStep(1);
                  }}
                >
                  <i className="fa-solid fa-mobile-screen" />
                  <span>Mobile OTP</span>
                </button>
                <button
                  type="button"
                  className={`ap-forgot-tab ${forgotMethod === 'email' ? 'active' : ''}`}
                  onClick={() => {
                    setForgotMethod('email');
                    setForgotStep(1);
                  }}
                >
                  <i className="fa-regular fa-envelope" />
                  <span>Email OTP</span>
                </button>
              </div>
            )}

            {forgotStep === 1 ? (
              <form onSubmit={handleRequestForgotOtp} className="ap-form">
                {forgotMethod === 'mobile' ? (
                  <div className="ap-field">
                    <label className="ap-label">Registered Mobile Number</label>
                    <div className="ap-phone-row">
                      <div className="ap-dial-wrap">
                        <select
                          className="ap-dial-select"
                          value={forgotCountryCode}
                          onChange={e => {
                            const selected = COUNTRIES.find(c => c.code === e.target.value) || COUNTRIES[0];
                            setForgotCountryCode(selected.code);
                            setForgotCountryDial(selected.dial);
                          }}
                        >
                          {COUNTRIES.map(c => (
                            <option key={`forgot-${c.code}-${c.dial}`} value={c.code}>
                              {c.flag} {c.dial}
                            </option>
                          ))}
                        </select>
                        <i className="fa-solid fa-chevron-down ap-dial-arrow" />
                      </div>

                      <div className="ap-input-wrap ap-phone-input-wrap">
                        <i className="fa-solid fa-mobile-screen ap-ico" />
                        <input
                          type="tel"
                          placeholder="98765 43210"
                          value={forgotMobile}
                          onChange={e => setForgotMobile(e.target.value)}
                          required
                          maxLength={15}
                          autoFocus
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ap-field">
                    <label className="ap-label">Registered Email Address</label>
                    <div className="ap-input-wrap">
                      <i className="fa-regular fa-envelope ap-ico" />
                      <input
                        type="email"
                        placeholder="you@company.com"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                <button type="submit" className="ap-btn" disabled={forgotLoading} style={{ marginTop: 10 }}>
                  {forgotLoading ? <span className="ap-spinner" /> : 'Send Reset OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="ap-form">
                <div className="ap-field">
                  <div className="ap-label-row">
                    <label className="ap-label">6-Digit OTP Code</label>
                    {forgotResendTimer > 0 ? (
                      <span className="ap-resend-timer">
                        <i className="fa-regular fa-clock" /> Resend in {forgotResendTimer}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="ap-resend-link"
                        onClick={handleRequestForgotOtp}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                  <div className="ap-input-wrap">
                    <i className="fa-solid fa-key ap-ico" />
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="123456"
                      value={forgotOtp}
                      onChange={e => setForgotOtp(e.target.value.replace(/\D/g, ''))}
                      style={{ letterSpacing: '5px', textAlign: 'center', fontWeight: 700, fontSize: '1.2rem' }}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <div className="ap-field">
                  <label className="ap-label">New Password</label>
                  <div className="ap-input-wrap">
                    <i className="fa-solid fa-lock ap-ico" />
                    <input
                      type={showNewPwd ? 'text' : 'password'}
                      placeholder="Min 6 characters"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="ap-eye"
                      onClick={() => setShowNewPwd(v => !v)}
                      tabIndex={-1}
                    >
                      <i className={`fa-regular ${showNewPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </button>
                  </div>
                </div>

                <div className="ap-field">
                  <label className="ap-label">Confirm New Password</label>
                  <div className="ap-input-wrap">
                    <i className="fa-solid fa-shield-halved ap-ico" />
                    <input
                      type={showConfirmNewPwd ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmNewPassword}
                      onChange={e => setConfirmNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="ap-eye"
                      onClick={() => setShowConfirmNewPwd(v => !v)}
                      tabIndex={-1}
                    >
                      <i className={`fa-regular ${showConfirmNewPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button
                    type="button"
                    className="ap-btn"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', boxShadow: 'none' }}
                    onClick={() => setForgotStep(1)}
                  >
                    Back
                  </button>
                  <button type="submit" className="ap-btn" disabled={forgotLoading}>
                    {forgotLoading ? <span className="ap-spinner" /> : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
